// @ts-nocheck
import { SettlementStaysFormFields } from 'types';
import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { PageBaseLayout } from 'components/layouts';
import { settingsTexts } from 'consts/text';
import { AxiosError } from 'axios';
import { parseSettlementStaysDTOIntoSettlementStaysFormFields } from 'utils/parsers/settlementStays/parseSettlementStaysDTOIntoSettlementStaysFormFields';
import { parseSettlementStaysFormFieldsIntoUpdateSettlementStaysDTO } from 'utils/parsers/settlementStays/parseSettlementStaysFormFieldsIntoUpdateSettlementStaysDTO';
import { SettlementStaysEditForm } from 'components/templates';
import { useEffect, useMemo } from 'react';

export const SettlementStaysEditPage = () => {
    const { form, formInitialData, setFormData } = useEditForm<SettlementStaysFormFields>();
    const { displaySuccess } = useSimpleNotification();

    const {
        data: settlementStaysData,
        isError: isSettlementStaysDataError,
        isFetching: isSettlementStaysDataFetching,
        error: settlementStaysDataError,
    } = useAppQuery('SETTLEMENT_STAYS', [], StaffHTTPService.settings.getSettlementStays, {
        retry: false,

        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (settlementStaysData) {
            const value = parseSettlementStaysDTOIntoSettlementStaysFormFields(settlementStaysData);
            setFormData(value);
        }
    }, [setFormData, settlementStaysData]);

    const {
        mutate: editSettlementStays,
        error: editSettlementStaysError,
        isLoading: isEditSettlementStaysLoading,
    } = useAppMutation(StaffHTTPService.settings.updateSettlementStays, {
        key: ['UPDATE_SETTLEMENT_STAYS'],
        onSuccess: () =>
            displaySuccess(settingsTexts.settlements.meals.successNotifications.settlementEdited),
        invalidateQueryKeys: [['SETTLEMENT_STAYS']],
    });

    const onEditSettlementStaysFinish = (settlementStaysFormFields: SettlementStaysFormFields) => {
        const parsedSettlementStaysFormFields =
            parseSettlementStaysFormFieldsIntoUpdateSettlementStaysDTO(settlementStaysFormFields);

        if (!parsedSettlementStaysFormFields) {
            return;
        }

        editSettlementStays({
            settlementStaysDTO: parsedSettlementStaysFormFields,
        });
    };

    const isSettlementStaysDataErrorOnFetching = () => {
        const code =
            settlementStaysDataError instanceof AxiosError
                ? settlementStaysDataError?.response?.data?.code
                : '';

        if (isSettlementStaysDataError) {
            if (code === 'SETTLEMENT_SETTINGS_NOT_FOUND') {
                return false;
            }
            return true;
        }
        return false;
    };

    const safeFormInitialData = useMemo(() => {
        if (!formInitialData) {
            return null;
        }
        // @ts-ignore: ignore type errors for extra fields
        return {
            ...formInitialData,
            // Payment type from main level
            paymentType: formInitialData.paymentType,
            // Settlement settings - prefer values from settlementSetting over main level
            stayFeeType:
                formInitialData.settlementSetting?.stayFeeType || formInitialData.stayFeeType,
            stayTimeType:
                formInitialData.settlementSetting?.stayTimeType || formInitialData.stayTimeType,
            timeRoundingUnitMinutes: formInitialData.settlementSetting
                ?.timeRoundingUnitMinutes as number,
            freeHoursAmount: formInitialData.settlementSetting?.freeHoursAmount as number,
            costPerHour: formInitialData.settlementSetting?.costPerHour as number,
            flatMonthlyFee: formInitialData.settlementSetting?.flatMonthlyFee as number,
            // Legacy amount field (keeping for backward compatibility)
            amount: formInitialData.settlementSetting?.costPerHour as number,
        };
    }, [formInitialData]);

    return (
        <PageBaseLayout isError={isSettlementStaysDataErrorOnFetching()}>
            <SettlementStaysEditForm
                form={form}
                initialValues={safeFormInitialData}
                axiosError={editSettlementStaysError}
                isLoading={isEditSettlementStaysLoading || isSettlementStaysDataFetching}
                onFinish={onEditSettlementStaysFinish}
            />
        </PageBaseLayout>
    );
};
